import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import './styles/nutrashore-theme.css';
import './theme.css';
import App from './App.jsx';
import { getMsalInstance, isMsalEnabled } from './lib/auth.js';
import * as Sentry from '@sentry/react';

// DEPLOY-VERIFY-1: bakes the deploy commit into the served bundle so a deploy
// workflow can grep the served frontend for this exact build, not just the
// backend SHA — a deploy can serve the right backend commit while the
// frontend bundle is stale/dead-code-tree-shook (see FIN-3's dead-file miss).
window.__GIT_SHA__ = import.meta.env.VITE_GIT_SHA ?? null;

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE ?? 'unknown',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'interaction_in_progress',
    ],
  });
}

// ── Tenant branding (portal.nutrashore.com?tenant=<slug>) ────────────────────
// Fires before React mounts so the login screen inherits tenant colors/logo.
// After MSAL auth, the ?tenant param is stripped from the URL (replaceState).
(async () => {
  const params = new URLSearchParams(window.location.search);
  // Default to 'nutrashore' so the tab title is always set even without ?tenant= param
  const tenant = params.get('tenant') || import.meta.env.VITE_TENANT_SLUG || 'nutrashore';
  try {
    const res = await fetch(`/api/tenant-config/${encodeURIComponent(tenant)}`);
    if (res.ok) {
      const config = await res.json();
      if (config.primaryColor) {
        document.documentElement.style.setProperty('--brand-primary', config.primaryColor);
      }
      if (config.accentColor) {
        document.documentElement.style.setProperty('--brand-accent', config.accentColor);
      }
      document.title = import.meta.env.VITE_TENANT_NAME || config.companyName || 'NutraShore';
      sessionStorage.setItem('tenantConfig', JSON.stringify(config));
    }
  } catch {
    // Non-fatal — fall back to defaults
  }
  // Strip ?tenant from URL so it doesn't persist into the app after login
  const clean = new URL(window.location.href);
  clean.searchParams.delete('tenant');
  window.history.replaceState({}, '', clean.toString());

  // MSAL Browser v3 requires initialize() before MsalProvider renders.
  // Skip entirely for email-auth tenants (Rochem, AMPAK) — MSAL is not used.
  if (isMsalEnabled) {
    // Clear stale interaction locks only when this is NOT a redirect return from Azure AD.
    // When Azure AD redirects back with #code= or ?code= in the URL, the
    // msal.interaction.status key holds the legitimate lock that was set by loginRedirect().
    // handleRedirectPromise() (called by MsalProvider) checks this key at line 103 of
    // RedirectClient.mjs and returns null immediately if it is absent — which leaves the
    // user unauthenticated and causes a redirect loop.
    // On a normal (non-redirect) page load any persisted lock is stale and safe to remove.
    const isAuthCodeResponse =
      window.location.hash.includes('code=') ||
      window.location.search.includes('code=');
    if (!isAuthCodeResponse) {
      for (const key of Object.keys(sessionStorage)) {
        if (key.includes('.interaction.status')) sessionStorage.removeItem(key);
      }
    }
    await getMsalInstance().initialize();
  }

  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
})();
